/**
 * How far along each of a feature's effects is, 0–1: the plume, the
 * eruption. A state sets where each should be; forward by one step eases
 * there over a few seconds, anything else is there at once (ADR-032,
 * "World features and states").
 */
export class Levels<K extends string> {
  readonly #now: Record<K, number>;
  readonly #from: Record<K, number>;
  readonly #to: Record<K, number>;
  #t = 1;
  #seconds = 1;

  constructor(initial: Record<K, number>) {
    this.#now = { ...initial };
    this.#from = { ...initial };
    this.#to = { ...initial };
  }

  /** Towards `target`: over `seconds`, or at once when `seconds` is 0. */
  set(target: Record<K, number>, seconds: number): void {
    for (const key of Object.keys(target) as K[]) {
      this.#from[key] = this.#now[key];
      this.#to[key] = target[key];
    }
    this.#seconds = Math.max(0, seconds);
    this.#t = this.#seconds === 0 ? 1 : 0;
    if (this.#t === 1) Object.assign(this.#now, this.#to);
  }

  update(dt: number): void {
    if (this.#t >= 1) return;
    this.#t = Math.min(1, this.#t + dt / this.#seconds);
    const u = this.#t * this.#t * (3 - 2 * this.#t);
    for (const key of Object.keys(this.#to) as K[]) {
      this.#now[key] = this.#from[key] + (this.#to[key] - this.#from[key]) * u;
    }
  }

  get(key: K): number {
    return this.#now[key];
  }

  /** Still easing towards its state. */
  get moving(): boolean {
    return this.#t < 1;
  }
}
